/**
 * EnumWizard.jsx
 * AI-Guided Enum Definition Wizard with Frontend-Controlled Processing
 * 
 * Flow:
 * 1. Discovery: Get list of enum columns (fast, no AI)
 * 2. Processing: Frontend loops through each column, calling API one at a time
 *    - Progress saved to DB, can resume if browser closes
 * 3. Review: All suggestions pre-loaded, user accepts/edits/skips/ignores instantly
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { 
  Hash, Loader2, Check, X, ChevronRight, ChevronLeft,
  Database, Table2, Sparkles, SkipForward, CheckCircle2,
  AlertCircle, Edit2, RefreshCw, Ban, Plus, Trash2, Play,
  Pause, Settings
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const EnumWizard = ({ onComplete, isAdmin }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [state, setState] = useState({
    step: 'idle', // idle, processing, review, manual-input, complete
    loading: true,
    
    // Processing state
    queue: [], // Columns to process
    currentIndex: 0,
    isProcessing: false,
    isPaused: false,
    processingErrors: [],
    
    // Review state
    pendingSuggestions: [],
    failedSuggestions: [],
    currentReviewIndex: 0,
    editedMappings: {},
    addedValues: [], // New values added by user during review
    
    // Manual input state
    manualInputColumn: null,
    manualInputValues: '',
    manualInputSuggestions: {},
    manualInputLoading: false,
    
    // Stats
    stats: {
      total: 0,
      to_process: 0,
      processed: 0,
      defined: 0,
      pending_review: 0,
      failed: 0
    }
  });
  
  const processingRef = useRef(false);
  const pauseRef = useRef(false);

  const loadInitialData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      // Get processing queue status
      const queueRes = await fetch(`${API}/api/learning/enum-processing-queue`, {
        headers: getAuthHeader()
      }).then(r => r.json());
      
      // Get pending suggestions for review
      const suggestionsRes = await fetch(`${API}/api/learning/pending-enum-suggestions`, {
        headers: getAuthHeader()
      }).then(r => r.json());
      
      const pendingSuggestions = suggestionsRes.suggestions || [];
      const failedSuggestions = suggestionsRes.failed || [];
      const queue = queueRes.columns || [];
      const stats = queueRes.stats || { total: 0, to_process: 0, processed: 0, defined: 0 };
      
      // Enhanced stats
      const enhancedStats = {
        ...stats,
        pending_review: pendingSuggestions.length,
        failed: failedSuggestions.length
      };
      
      // Determine step based on state - show idle so user can choose
      let step = 'idle';
      
      setState(prev => ({
        ...prev,
        loading: false,
        queue,
        pendingSuggestions,
        failedSuggestions,
        stats: enhancedStats,
        step
      }));
      
    } catch (error) {
      console.error('Failed to load enum data:', error);
      toast.error('Failed to load enum data');
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);
  // AUTO-LEARNING: When moving to a new review column, automatically apply learned definitions
  useEffect(() => {
    const applyLearnedDefinitionsAutomatically = async () => {
      if (state.step === 'review' && state.pendingSuggestions.length > 0) {
        const currentSuggestion = state.pendingSuggestions[state.currentReviewIndex];
        if (currentSuggestion?.column) {
          await fetchAndApplyLearnedSuggestions(currentSuggestion.column, true); // silent=true
        }
      }
    };
    
    applyLearnedDefinitionsAutomatically();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentReviewIndex, state.step]);

  const clearAndRestart = async () => {
    if (!window.confirm('This will delete all pending suggestions and let you start fresh. Continue?')) {
      return;
    }
    
    setState(prev => ({ ...prev, loading: true }));
    try {
      await fetch(`${API}/api/learning/clear-enum-suggestions`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      toast.success('Cleared all pending suggestions. Ready to start fresh!');
      await loadInitialData();
    } catch (error) {
      console.error('Failed to clear:', error);
      toast.error('Failed to clear suggestions');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const startProcessing = async () => {
    if (state.queue.length === 0) {
      toast.info('No enum columns to process');
      return;
    }
    
    setState(prev => ({ 
      ...prev, 
      step: 'processing', 
      isProcessing: true, 
      isPaused: false,
      currentIndex: 0,
      processingErrors: []
    }));
    
    processingRef.current = true;
    pauseRef.current = false;
    
    await processQueue();
  };

  const pauseProcessing = () => {
    pauseRef.current = true;
    setState(prev => ({ ...prev, isPaused: true, isProcessing: false }));
    toast.info('Processing paused. You can resume later.');
  };

  const resumeProcessing = async () => {
    pauseRef.current = false;
    processingRef.current = true;
    setState(prev => ({ ...prev, isPaused: false, isProcessing: true }));
    await processQueue();
  };

  const processQueue = async () => {
    const { queue } = state;
    let currentIdx = state.currentIndex;
    const errors = [...state.processingErrors];
    
    while (currentIdx < queue.length && processingRef.current && !pauseRef.current) {
      const col = queue[currentIdx];
      
      setState(prev => ({ ...prev, currentIndex: currentIdx }));
      
      try {
        const response = await fetch(`${API}/api/learning/process-single-enum`, {
          method: 'POST',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            server: col.server,
            database: col.database,
            table: col.table,
            column: col.column
          })
        }).then(r => r.json());
        
        if (!response.success) {
          errors.push({ column: col, error: response.error });
          // Save error to DB so it persists
          await fetch(`${API}/api/learning/save-enum-error`, {
            method: 'POST',
            headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              server: col.server,
              database: col.database,
              table: col.table,
              column: col.column,
              error: response.error
            })
          }).catch(() => {});
        }
        
      } catch (error) {
        console.error(`Error processing ${col.table}.${col.column}:`, error);
        errors.push({ column: col, error: error.message });
      }
      
      currentIdx++;
      setState(prev => ({ 
        ...prev, 
        currentIndex: currentIdx,
        processingErrors: errors
      }));
      
      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Processing complete or paused
    if (currentIdx >= queue.length) {
      processingRef.current = false;
      toast.success(`Processing complete! ${queue.length - errors.length} succeeded, ${errors.length} failed`);
      
      // Reload to get pending suggestions for review
      await loadInitialData();
    }
  };

  const handleAccept = async () => {
    const suggestion = state.pendingSuggestions[state.currentReviewIndex];
    if (!suggestion) return;
    
    try {
      // Save any edits as learned corrections
      const originalSuggestions = suggestion.suggestions || {};
      const edited = state.editedMappings;
      
      // Find values that were edited
      for (const [val, meaning] of Object.entries(edited)) {
        if (meaning && meaning !== originalSuggestions[val]) {
          // Save as learned correction
          await fetch(`${API}/api/learning/learned-enum-correction`, {
            method: 'POST',
            headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              database: suggestion.database,
              value: val,
              meaning: meaning
            })
          });
        }
      }
      
      // Include manually added values
      const addedValuesMap = {};
      for (const added of state.addedValues) {
        if (added.value && added.meaning) {
          addedValuesMap[added.value] = added.meaning;
          // Also save as learned correction for future use
          await fetch(`${API}/api/learning/learned-enum-correction`, {
            method: 'POST',
            headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              database: suggestion.database,
              value: added.value,
              meaning: added.meaning
            })
          });
        }
      }
      
      // Save as enum definition (original + edited + added)
      const finalValues = { ...originalSuggestions, ...edited, ...addedValuesMap };
      
      await fetch(`${API}/api/learning/enum-definition`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server: suggestion.server,
          database: suggestion.database,
          table: suggestion.table,
          column: suggestion.column,
          definitions: finalValues,
          notes: ''
        })
      });
      
      // Mark suggestion as accepted
      await fetch(`${API}/api/learning/accept-enum-suggestion/${suggestion.id}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      
      toast.success('Enum definition saved!');
      moveToNextReview();
      
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save definition');
    }
  };

  const handleSkip = async () => {
    const suggestion = state.pendingSuggestions[state.currentReviewIndex];
    if (!suggestion) return;
    
    try {
      await fetch(`${API}/api/learning/skip-enum-suggestion/${suggestion.id}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      toast.info('Skipped - you can review later');
      moveToNextReview();
    } catch (error) {
      toast.error('Failed to skip');
    }
  };

  const handleIgnore = async () => {
    const suggestion = state.pendingSuggestions[state.currentReviewIndex];
    if (!suggestion) return;
    
    try {
      await fetch(`${API}/api/learning/ignore-enum-suggestion/${suggestion.id}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      toast.info('Marked as not an enum');
      moveToNextReview();
    } catch (error) {
      toast.error('Failed to ignore');
    }
  };

  const moveToNextReview = () => {
    const nextIndex = state.currentReviewIndex + 1;
    
    if (nextIndex >= state.pendingSuggestions.length) {
      // All done!
      setState(prev => ({ ...prev, step: 'complete' }));
      if (onCompleteRef.current) onCompleteRef.current();
    } else {
      setState(prev => ({
        ...prev,
        currentReviewIndex: nextIndex,
        editedMappings: {}, // Reset edits for next
        addedValues: [] // Reset added values for next
      }));
    }
  };

  const handleMappingChange = (value, meaning) => {
    setState(prev => ({
      ...prev,
      editedMappings: { ...prev.editedMappings, [value]: meaning }
    }));
  };

  // Add a new enum value during review
  const addNewValue = () => {
    setState(prev => ({
      ...prev,
      addedValues: [...prev.addedValues, { value: '', meaning: '' }]
    }));
  };

  // Update an added value
  const updateAddedValue = (index, field, newValue) => {
    setState(prev => {
      const updated = [...prev.addedValues];
      updated[index] = { ...updated[index], [field]: newValue };
      return { ...prev, addedValues: updated };
    });
  };

  // Remove an added value
  const removeAddedValue = (index) => {
    setState(prev => ({
      ...prev,
      addedValues: prev.addedValues.filter((_, i) => i !== index)
    }));
  };

  // Fetch learned suggestions for a column and apply them to the current review
  // silent=true means don't show toast (for auto-apply), silent=false for manual click
  const fetchAndApplyLearnedSuggestions = async (column, silent = false) => {
    try {
      const response = await fetch(`${API}/api/learning/enum-suggestions/${encodeURIComponent(column)}`, {
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_suggestions && data.suggestions.length > 0) {
          // Find the suggestion with the MOST definitions (most comprehensive)
          let bestSuggestion = data.suggestions[0];
          let maxDefinitions = Object.keys(bestSuggestion.definitions || {}).length;
          
          for (const suggestion of data.suggestions) {
            const defCount = Object.keys(suggestion.definitions || {}).length;
            if (defCount > maxDefinitions) {
              bestSuggestion = suggestion;
              maxDefinitions = defCount;
            }
          }
          
          console.log('[EnumWizard] Best suggestion has', maxDefinitions, 'definitions from', bestSuggestion.from_table || bestSuggestion.example_table);
          
          if (bestSuggestion && bestSuggestion.definitions && maxDefinitions > 0) {
            // Update the editedMappings with learned suggestions
            const newMappings = { ...state.editedMappings };
            const currentSuggestion = state.pendingSuggestions[state.currentReviewIndex];
            const currentValues = currentSuggestion?.values || [];
            
            let appliedCount = 0;
            for (const val of currentValues) {
              const valStr = String(val);
              // Apply learned definition - overwrite AI suggestions but not user edits
              if (bestSuggestion.definitions[valStr]) {
                newMappings[valStr] = bestSuggestion.definitions[valStr];
                appliedCount++;
              }
            }
            
            if (appliedCount > 0) {
              setState(prev => ({ 
                ...prev, 
                editedMappings: newMappings 
              }));
              
              if (!silent) {
                toast.success(`Applied ${appliedCount} learned definitions from ${bestSuggestion.from_table || bestSuggestion.example_table || 'previous definitions'}`, {
                  duration: 3000
                });
              }
            } else if (!silent) {
              toast.info('No matching definitions found for current values', { duration: 2000 });
            }
            
            return data.suggestions;
          }
        } else if (!silent) {
          toast.info('No learned definitions found for this column', { duration: 2000 });
        }
      }
    } catch (error) {
      console.error('Failed to fetch learned suggestions:', error);
      if (!silent) {
        toast.error('Failed to fetch learned definitions');
      }
    }
    return [];
  };

  // Manual input functions
  const startManualInput = (column) => {
    setState(prev => ({
      ...prev,
      step: 'manual-input',
      manualInputColumn: column,
      manualInputValues: '',
      manualInputSuggestions: {},
      editedMappings: {}
    }));
  };

  // Auto-process manual values: get AI suggestions and move to Ready to Review
  const processManualValues = async () => {
    const { manualInputColumn, manualInputValues } = state;
    if (!manualInputValues.trim()) {
      toast.error('Please paste your SQL output first');
      return;
    }
    
    // Parse values (handle various formats: newlines, commas, pipes, MySQL output format)
    const rawValues = manualInputValues
      .split(/[\n,|]+/)
      .map(v => v.trim())
      .filter(v => v && v !== 'NULL' && !v.startsWith('+') && !v.startsWith('-') && !v.includes('---'));
    
    if (rawValues.length === 0) {
      toast.error('No valid values found. Paste output from your SQL query.');
      return;
    }
    
    setState(prev => ({ ...prev, manualInputLoading: true }));
    
    try {
      // Call API to process and save to ready-to-review
      const response = await fetch(`${API}/api/learning/process-manual-enum`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server: manualInputColumn.server,
          database: manualInputColumn.database,
          table: manualInputColumn.table,
          column: manualInputColumn.column,
          values: rawValues
        })
      }).then(r => r.json());
      
      if (response.success) {
        toast.success(`Processed! ${rawValues.length} values with AI suggestions moved to Ready to Review.`);
        // Reload and go back to idle
        await loadInitialData();
        setState(prev => ({ ...prev, step: 'idle', manualInputLoading: false }));
      } else {
        toast.error(response.error || 'Failed to process');
        setState(prev => ({ ...prev, manualInputLoading: false }));
      }
    } catch (error) {
      console.error('Process error:', error);
      toast.error('Failed to process values');
      setState(prev => ({ ...prev, manualInputLoading: false }));
    }
  };

  // Ignore a failed enum column (mark as not-an-enum)
  const ignoreFailedEnum = async (column) => {
    try {
      await fetch(`${API}/api/learning/ignore-failed-enum`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          server: column.server,
          database: column.database,
          table: column.table,
          column: column.column
        })
      });
      toast.success('Marked as not an enum');
      await loadInitialData();
    } catch (error) {
      toast.error('Failed to ignore');
    }
  };

  // ========== RENDER FUNCTIONS ==========

  const renderIdle = () => (
    <div className="text-center py-8">
      <Hash className="w-16 h-16 text-purple-400 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-gray-800 mb-2">AI-Guided Enum Definition</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        Define what enum values mean (status=1 → "Active"). The AI will scan your schema, 
        query actual values, and suggest meanings. You review and approve.
      </p>
      
      {/* Stats */}
      <div className="flex items-center justify-center flex-wrap gap-6 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-800">{state.stats.total}</div>
          <div className="text-sm text-gray-500">Total Columns</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-amber-600">{state.stats.to_process}</div>
          <div className="text-sm text-gray-500">To Process</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{state.stats.pending_review || 0}</div>
          <div className="text-sm text-gray-500">Ready to Review</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{state.stats.defined}</div>
          <div className="text-sm text-gray-500">Defined</div>
        </div>
        {state.stats.failed > 0 && (
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{state.stats.failed}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
        )}
      </div>
      
      {/* Action buttons based on state */}
      <div className="flex flex-col items-center gap-3">
        {/* Primary action: Process or Review */}
        {state.stats.to_process > 0 && (
          <Button 
            onClick={startProcessing} 
            className="bg-purple-500 hover:bg-purple-600"
          >
            <Play className="w-4 h-4 mr-2" />
            {state.stats.pending_review > 0 ? 'Continue' : 'Start'} Processing ({state.stats.to_process} columns)
          </Button>
        )}
        
        {state.stats.pending_review > 0 && (
          <Button 
            onClick={() => setState(prev => ({ ...prev, step: 'review' }))} 
            className="bg-green-500 hover:bg-green-600"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Review {state.stats.pending_review} Suggestions
          </Button>
        )}
        
        {state.stats.to_process === 0 && state.stats.pending_review === 0 && state.stats.defined > 0 && (
          <div className="text-gray-500">
            <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
            All enum columns have been defined!
          </div>
        )}
        
        {/* Secondary action: Clear and restart */}
        {(state.stats.pending_review > 0 || state.stats.failed > 0) && (
          <Button 
            variant="outline" 
            onClick={clearAndRestart}
            className="text-red-500 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All & Restart Fresh
          </Button>
        )}
      </div>
      
      {/* Failed items warning */}
      {state.failedSuggestions && state.failedSuggestions.length > 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg max-w-lg mx-auto text-left">
          <h4 className="font-medium text-red-800 mb-2">
            {state.failedSuggestions.length} columns failed to process
          </h4>
          <p className="text-sm text-red-600 mb-3">
            Enter values manually or mark as "Not an Enum" to ignore.
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {state.failedSuggestions.map((f, i) => (
              <div key={i} className="bg-white p-3 rounded border border-red-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800 text-sm">
                    {f.database}.{f.table}.{f.column}
                  </span>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => startManualInput(f)}
                      className="text-xs"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Enter Values
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => ignoreFailedEnum(f)}
                      className="text-xs text-gray-500"
                    >
                      <Ban className="w-3 h-3 mr-1" />
                      Ignore
                    </Button>
                  </div>
                </div>
                <code className="text-xs text-gray-500 block bg-gray-50 p-1 rounded mt-1">
                  SELECT DISTINCT `{f.column}` FROM `{f.table}` LIMIT 50;
                </code>
                {f.error && (
                  <p className="text-xs text-red-500 mt-1">Error: {f.error.substring(0, 80)}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderProcessing = () => {
    const progress = state.queue.length > 0 
      ? ((state.currentIndex) / state.queue.length) * 100 
      : 0;
    const currentCol = state.queue[state.currentIndex];
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-800">
            Processing Enum Columns
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {state.currentIndex} of {state.queue.length} processed
          </p>
        </div>
        
        <Progress value={progress} className="h-3" />
        
        {currentCol && (
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-500">Currently processing:</p>
            <p className="font-medium text-gray-800">
              {currentCol.server} → {currentCol.database} → {currentCol.table}.{currentCol.column}
            </p>
          </div>
        )}
        
        {state.processingErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">
              {state.processingErrors.length} errors so far (will continue)
            </p>
          </div>
        )}
        
        <div className="flex justify-center gap-3">
          {state.isPaused ? (
            <Button onClick={resumeProcessing} className="bg-green-500 hover:bg-green-600">
              <Play className="w-4 h-4 mr-2" />
              Resume
            </Button>
          ) : (
            <Button onClick={pauseProcessing} variant="outline">
              <Pause className="w-4 h-4 mr-2" />
              Pause
            </Button>
          )}
        </div>
        
        <p className="text-xs text-gray-400 text-center">
          You can close this page and come back later. Progress is saved.
        </p>
      </div>
    );
  };

  const renderReview = () => {
    const suggestion = state.pendingSuggestions[state.currentReviewIndex];
    if (!suggestion) return null;
    
    const progress = ((state.currentReviewIndex + 1) / state.pendingSuggestions.length) * 100;
    const values = suggestion.values || [];
    const suggestions = suggestion.suggestions || {};
    const learnedApplied = suggestion.learned_applied || {};
    
    return (
      <div className="space-y-4">
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">
              Reviewing: <span className="font-medium">{suggestion.table}.{suggestion.column}</span>
            </span>
            <span className="text-gray-500">
              {state.currentReviewIndex + 1} of {state.pendingSuggestions.length}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Location breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
          <Database className="w-4 h-4" />
          <span>{suggestion.server}</span>
          <ChevronRight className="w-3 h-3" />
          <span>{suggestion.database}</span>
          <ChevronRight className="w-3 h-3" />
          <Table2 className="w-4 h-4" />
          <span className="font-medium text-gray-700">{suggestion.table}</span>
        </div>

        {/* Values and mappings */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-purple-600" />
                {suggestion.column}
                {suggestion.row_count && (
                  <span className="text-xs text-gray-400 font-normal">
                    ({suggestion.row_count.toLocaleString()} rows)
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAndApplyLearnedSuggestions(suggestion.column, false)}
                className="text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                title="Re-apply learned definitions from other tables with same column name"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                Re-apply Learned
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {values.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {values.map((val, idx) => {
                  const isLearned = learnedApplied[val];
                  // Get AI suggestion or generate a default hint
                  let aiSuggestion = suggestions[val] || '';
                  if (!aiSuggestion) {
                    // Generate a basic hint from the value
                    aiSuggestion = String(val).replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                    aiSuggestion = aiSuggestion.charAt(0).toUpperCase() + aiSuggestion.slice(1).toLowerCase();
                  }
                  const currentMeaning = state.editedMappings[val] !== undefined 
                    ? state.editedMappings[val] 
                    : aiSuggestion;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`px-3 py-2 rounded font-mono text-sm min-w-[100px] text-center ${
                        isLearned ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {val}
                        {isLearned && <span className="ml-1 text-xs">✨</span>}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <Input
                        value={currentMeaning}
                        onChange={(e) => handleMappingChange(val, e.target.value)}
                        placeholder="Enter meaning..."
                        className={`flex-1 ${suggestions[val] ? 'border-purple-200 bg-purple-50' : ''}`}
                      />
                      {suggestions[val] && (
                        <span className="text-xs text-purple-500">AI</span>
                      )}
                    </div>
                  );
                })}
                
                {/* Added values by user */}
                {state.addedValues.map((added, idx) => (
                  <div key={`added-${idx}`} className="flex items-center gap-3">
                    <Input
                      value={added.value}
                      onChange={(e) => updateAddedValue(idx, 'value', e.target.value)}
                      placeholder="Value (e.g. 3)"
                      className="w-[100px] font-mono text-sm bg-green-50 border-green-200"
                    />
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <Input
                      value={added.meaning}
                      onChange={(e) => updateAddedValue(idx, 'meaning', e.target.value)}
                      placeholder="Meaning (e.g. Suspended)"
                      className="flex-1 bg-green-50 border-green-200"
                    />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeAddedValue(idx)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                {/* Add Value button */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addNewValue}
                  className="text-green-600 border-green-200 hover:bg-green-50 mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Missing Value
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-gray-500 text-center py-2">No values found - add manually:</p>
                {/* Added values by user */}
                {state.addedValues.map((added, idx) => (
                  <div key={`added-${idx}`} className="flex items-center gap-3">
                    <Input
                      value={added.value}
                      onChange={(e) => updateAddedValue(idx, 'value', e.target.value)}
                      placeholder="Value (e.g. 1)"
                      className="w-[100px] font-mono text-sm bg-green-50 border-green-200"
                    />
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <Input
                      value={added.meaning}
                      onChange={(e) => updateAddedValue(idx, 'meaning', e.target.value)}
                      placeholder="Meaning (e.g. Active)"
                      className="flex-1 bg-green-50 border-green-200"
                    />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeAddedValue(idx)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addNewValue}
                  className="text-green-600 border-green-200 hover:bg-green-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Value
                </Button>
              </div>
            )}
            
            {Object.keys(learnedApplied).length > 0 && (
              <p className="text-xs text-amber-600">
                ✨ = Pre-filled from previous corrections in this database
              </p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={handleIgnore} className="text-gray-500">
            <Ban className="w-4 h-4 mr-2" />
            Not an Enum
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSkip}>
              <SkipForward className="w-4 h-4 mr-2" />
              Skip
            </Button>
            <Button onClick={handleAccept} className="bg-green-500 hover:bg-green-600">
              <Check className="w-4 h-4 mr-2" />
              Accept & Next
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderComplete = () => (
    <div className="text-center py-8">
      <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-gray-800 mb-2">Enum Review Complete!</h3>
      <p className="text-gray-500 mb-6">
        All enum suggestions have been reviewed.
      </p>
      <Button onClick={() => {
        loadInitialData();
        setState(prev => ({ ...prev, step: 'idle', currentReviewIndex: 0 }));
      }}>
        <RefreshCw className="w-4 h-4 mr-2" />
        Check for More
      </Button>
    </div>
  );

  const renderManualInput = () => {
    const col = state.manualInputColumn;
    if (!col) return null;
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => setState(prev => ({ ...prev, step: 'idle' }))}
            className="text-gray-500"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h3 className="font-medium text-gray-800">Manual Value Entry</h3>
          <div />
        </div>

        {/* Column info */}
        <Card className="border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Hash className="w-5 h-5 text-purple-600" />
              <div>
                <div className="font-medium text-gray-800">{col.table}.{col.column}</div>
                <div className="text-sm text-gray-500">{col.server} → {col.database}</div>
              </div>
            </div>
            
            {/* SQL Query to run */}
            <div className="bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-sm mb-3">
              <p className="text-gray-500 text-xs mb-1">-- Run this in your MySQL terminal:</p>
              SELECT DISTINCT `{col.column}` FROM `{col.database}`.`{col.table}` LIMIT 50;
            </div>
            
            {/* Input area */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Paste the SQL output here:
              </label>
              <textarea
                className="w-full h-32 p-3 border rounded-lg font-mono text-sm"
                placeholder={`Paste your MySQL output directly:\n+--------+\n| status |\n+--------+\n| ACTIVE |\n| PENDING |\n+--------+`}
                value={state.manualInputValues}
                onChange={(e) => setState(prev => ({ ...prev, manualInputValues: e.target.value }))}
              />
              <p className="text-xs text-gray-500">
                Paste directly from MySQL - headers, pipes, and dashes will be filtered automatically.
              </p>
            </div>
            
            <div className="flex gap-2 mt-3">
              <Button 
                onClick={processManualValues}
                disabled={state.manualInputLoading || !state.manualInputValues.trim()}
                className="bg-purple-500 hover:bg-purple-600"
              >
                {state.manualInputLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Process & Add to Review Queue
              </Button>
              <Button 
                variant="outline"
                onClick={() => ignoreFailedEnum(col)}
                className="text-gray-500"
              >
                <Ban className="w-4 h-4 mr-2" />
                Not an Enum
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (state.loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          <span className="ml-3 text-gray-600">Loading enum data...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Hash className="w-5 h-5 text-purple-600" />
          Enum Definitions
          {state.stats.defined > 0 && (
            <span className="text-sm font-normal text-gray-500">
              ({state.stats.defined} defined)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {state.step === 'idle' && renderIdle()}
        {state.step === 'processing' && renderProcessing()}
        {state.step === 'review' && renderReview()}
        {state.step === 'manual-input' && renderManualInput()}
        {state.step === 'complete' && renderComplete()}
      </CardContent>
    </Card>
  );
};

export default EnumWizard;
